import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import SearchBox from '../../components/SearchBox';
import Loader from '../../components/Loader';

const PAGE_SIZE = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

function expiryLabel(expiryDate) {
  const daysLeft = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / DAY_MS);
  if (daysLeft <= 0) return <span className="badge badge-danger">Expired</span>;
  if (daysLeft <= 3) return <span className="badge badge-danger">{daysLeft} day{daysLeft === 1 ? '' : 's'} left</span>;
  if (daysLeft <= 7) return <span className="badge badge-warning">{daysLeft} days left</span>;
  return <span className="badge badge-muted">{daysLeft} days left</span>;
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [plans, setPlans] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = (term = searchTerm, pg = page) => {
    setLoading(true);
    apiCall(client.get('/users/admin/get_users', { params: { page: String(pg), searchTerm: term } }))
      .then((data) => { setUsers(data.data); setTotalCount(data.totalCount); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(searchTerm, page), [page]);
  useEffect(() => { apiCall(client.get('/credit_plan/admin/get_list')).then(setPlans).catch(() => setPlans([])); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(searchTerm, 1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
    load('', 1);
  };

  const toggleField = async (row, field) => {
    try {
      await apiCall(client.patch('/users/admin/update_user_status', {
        uniqueId: row.uniqueId, active: row.active, blocked: row.blocked, [field]: !row[field]
      }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await apiCall(client.post('/credit_plan/admin/assign', { userId: assigning.uniqueId, planId: selectedPlan }));
      setAssigning(null);
      setSelectedPlan('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'uniqueId', label: 'ID' },
    { key: 'active', label: 'Active', render: (row) => <StatusBadge value={row.active} /> },
    {
      key: 'blocked', label: 'Blocked',
      render: (row) => row.blocked ? <span className="badge badge-danger">Blocked</span> : <span className="badge badge-muted">No</span>
    },
    { key: 'plan', label: 'Plan', render: (row) => row.planName || <span className="badge badge-muted">No active plan</span> },
    {
      key: 'expiry', label: 'Expiry',
      render: (row) => {
        if (!row.planExpiryDate) return '—';
        return (
          <>
            {new Date(row.planExpiryDate).toLocaleDateString()}{' '}
            {expiryLabel(row.planExpiryDate)}
          </>
        );
      }
    },
    {
      key: 'actions', label: '', render: (row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => toggleField(row, 'active')}>
            {row.active ? 'Deactivate' : 'Activate'}
          </button>
          <button className="btn btn-danger-outline btn-sm" onClick={() => toggleField(row, 'blocked')}>
            {row.blocked ? 'Unblock' : 'Block'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setAssigning(row)}>Assign plan</button>
        </div>
      )
    },
  ];

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div>
      <h1>Users</h1>
      {error && <div className="form-error">{error}</div>}

      <form onSubmit={handleSearch} className="page-toolbar">
        <SearchBox
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={handleClearSearch}
          placeholder="Search name, email, phone..."
        />
        <button type="submit" className="btn btn-outline btn-sm">Search</button>
      </form>

      <div className="card">
        {loading ? (
          <Loader label="Loading users..." />
        ) : (
          <>
            <DataTable columns={columns} rows={users} emptyMessage="No users found." />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
              <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </>
        )}
      </div>

      {assigning && (
        <div className="modal-backdrop" onClick={() => setAssigning(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Assign plan to {assigning.name}</h3>
            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label>Plan</label>
                <select required value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
                  <option value="">Select a plan</option>
                  {plans.map((p) => <option key={p.uniqueId} value={p.uniqueId}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary">Assign</button>
                <button type="button" className="btn btn-outline" onClick={() => setAssigning(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
